import React, { useState, useEffect } from 'react';
import { FiChevronDown, FiChevronUp, FiChevronRight, FiAward, FiTruck, FiCheck, FiShield, FiZoomIn } from 'react-icons/fi';
import { FaYoutube, FaWhatsapp } from 'react-icons/fa';
import { SiX } from 'react-icons/si';
import { AiOutlineInstagram } from 'react-icons/ai';
import { BsFacebook } from 'react-icons/bs';
import { FaTape } from 'react-icons/fa6';
import { Link, useNavigate } from 'react-router-dom';
import { useCart } from '../../context/CartContext';
import { useUI } from '../../context/UIContext';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import toast from 'react-hot-toast';
import api from '../../services/api';
import GalleryModal from '../common/GalleryModal';
import SizeChartModal from '../common/SizeChartModal';

const ProductDetail = ({ product, initialSize = '' }) => {
    const [selectedSize, setSelectedSize] = useState('');
    const [selectedColor, setSelectedColor] = useState('');
    const [quantity, setQuantity] = useState(1);
    const [openAccordion, setOpenAccordion] = useState(null);
    const [activeImage, setActiveImage] = useState('');
    const [isGalleryOpen, setIsGalleryOpen] = useState(false);
    const [galleryIndex, setGalleryIndex] = useState(0);
    const [showSizeChart, setShowSizeChart] = useState(false);
    const [settings, setSettings] = useState({
        saleBoxEnabled: true,
        saleBoxContent: '⚠️ LAST WEEK OF SALE ⚠️',
        saleBoxColor: '#dc2626'
    });
    const [socialLinks, setSocialLinks] = useState([]);

    const { addToCart } = useCart();
    const { openCart } = useUI();
    const { isAuthenticated } = useAuth();
    const navigate = useNavigate();
    const { theme } = useTheme();

    useEffect(() => {
        const fetchSettings = async () => {
            try {
                const res = await api.get('/settings/announcement');
                setSettings({
                    saleBoxEnabled: res.saleBoxEnabled !== undefined ? res.saleBoxEnabled : true,
                    saleBoxContent: res.saleBoxContent || '⚠️ LAST WEEK OF SALE ⚠️',
                    saleBoxColor: res.saleBoxColor || '#dc2626'
                });
            } catch (err) {
                console.error('Failed to fetch settings');
            }
        };
        fetchSettings();
    }, []);

    useEffect(() => {
        const fetchSocialLinks = async () => {
            try {
                const footer = await api.get('/footer');
                if (footer.socialLinks) {
                    setSocialLinks(footer.socialLinks);
                }
            } catch (err) {
                console.error('Failed to fetch social links', err);
            }
        };
        fetchSocialLinks();
    }, []);

    useEffect(() => {
        if (product?.images?.length > 0) setActiveImage(product.images[0]);
        const variants = product.variants || [];
        const sizes = [...new Set(variants.map(v => v.size).filter(Boolean))];
        const colors = [...new Set(variants.map(v => v.color).filter(Boolean))];
        
        let defaultSize = sizes.length > 0 ? sizes[0] : '';
        if (initialSize && sizes.includes(initialSize)) {
            defaultSize = initialSize;
        }
        
        let defaultColor = colors.length > 0 ? colors[0] : '';
        if (defaultSize && variants.length > 0) {
            const availableColor = variants.find(v => v.size === defaultSize && v.inStock)?.color;
            if (availableColor) {
                defaultColor = availableColor;
            }
        }
        
        setSelectedColor(defaultColor);
        setSelectedSize(defaultSize);
        setQuantity(1);
    }, [product, initialSize]);

    if (!product) return null;

    const images = product.images?.length > 0 ? product.images : ['https://via.placeholder.com/1080'];
    const variants = product.variants || [];
    const sizes = [...new Set(variants.map(v => v.size).filter(Boolean))];
    const colors = [...new Set(variants.map(v => v.color).filter(Boolean))];
    
    let discountPercent = product.discount || (product.originalPrice > product.price ? Math.round(((product.originalPrice - product.price) / product.originalPrice) * 100) : 0);

    const toggleAccordion = (section) => setOpenAccordion(openAccordion === section ? null : section);

    const handleAddToCart = () => {
        if (colors.length > 0 && !selectedColor) {
            setSelectedColor(colors[0]);
            toast.info('Auto-selected first available color');
            return;
        }
        if (sizes.length > 0 && !selectedSize) {
            setSelectedSize(sizes[0]);
            toast.info('Auto-selected first available size');
            return;
        }
        
        const variant = variants.find(v => v.size === selectedSize && v.color === selectedColor);
        if (!variant || !variant.inStock || variant.quantity <= 0) {
            toast.error('Selected variant is out of stock');
            return;
        }
        
        if (quantity > variant.quantity) {
            toast.error(`Only ${variant.quantity} items available in stock`);
            setQuantity(variant.quantity);
            return;
        }

        addToCart(product, quantity, { size: selectedSize, color: selectedColor });
        openCart();
    };

    const handleBuyNow = () => {
        if (colors.length > 0 && !selectedColor) {
            setSelectedColor(colors[0]);
            toast.info('Auto-selected first available color');
            return;
        }
        if (sizes.length > 0 && !selectedSize) {
            setSelectedSize(sizes[0]);
            toast.info('Auto-selected first available size');
            return;
        }
        
        const variant = variants.find(v => v.size === selectedSize && v.color === selectedColor);
        if (!variant || !variant.inStock || variant.quantity <= 0) {
            toast.error('Selected variant is out of stock');
            return;
        }

        if (quantity > variant.quantity) {
            toast.error(`Only ${variant.quantity} items available in stock`);
            setQuantity(variant.quantity);
            return;
        }

        addToCart(product, quantity, { size: selectedSize, color: selectedColor });
        navigate('/checkout');
    };

    const selectedVariant = variants.find(v => v.size === selectedSize && v.color === selectedColor);
    const isVariantInStock = selectedVariant ? (selectedVariant.inStock && selectedVariant.quantity > 0) : false;
    const isProductCompletelyOutOfStock = variants.every(v => !v.inStock || v.quantity <= 0);

    const primaryColor = theme.primaryColor;
    const secondaryColor = theme.secondaryColor;
    const textColor = theme.textColor;

    const isSizeInStock = (size) => variants.some(v => v.size === size && v.inStock && v.quantity > 0);
    const isColorInStock = (color) => {
        if (!selectedSize) return variants.some(v => v.color === color && v.inStock && v.quantity > 0);
        return variants.some(v => v.color === color && v.size === selectedSize && v.inStock && v.quantity > 0);
    };

    const openGallery = (index) => {
        setGalleryIndex(index);
        setIsGalleryOpen(true);
    };

    const closeGallery = () => setIsGalleryOpen(false);
    const nextGalleryImage = () => setGalleryIndex((prev) => (prev + 1) % images.length);
    const prevGalleryImage = () => setGalleryIndex((prev) => (prev - 1 + images.length) % images.length);

    const getSocialLink = (iconName) => {
        const link = socialLinks.find(s => s.icon === iconName);
        return link ? link.link : '#';
    };

    return (
        <div className="w-full bg-white min-h-screen py-10 px-4 md:px-10 pb-24 lg:pb-10">
            <div className="flex flex-col lg:flex-row gap-8 max-w-[1550px] mx-auto">
                <div className="flex flex-col-reverse lg:flex-row gap-2 lg:w-[55%] lg:sticky lg:top-10 h-fit">
                    <div className="flex lg:flex-col gap-2 overflow-x-auto lg:overflow-y-auto hide-scrollbar lg:max-h-[600px] lg:w-[90px]">
                        {images.map((img, idx) => (
                            <img key={idx} src={img} alt="thumbnail" onClick={() => setActiveImage(img)} className={`w-[80px] h-[100px] lg:h-[150px] object-cover border cursor-pointer transition-colors flex-shrink-0 ${activeImage === img ? 'border-black' : 'border-gray-200 hover:border-gray-400'}`} />
                        ))}
                    </div>
                    <div className="flex-1 min-h-[400px] lg:min-h-[600px] bg-gray-50 flex items-center justify-center relative">
                        <img src={activeImage || images[0]} alt="Product" className="w-full h-full object-contain" />
                        <button
                            onClick={() => openGallery(images.indexOf(activeImage) !== -1 ? images.indexOf(activeImage) : 0)}
                            className="absolute bottom-4 right-4 bg-white/80 hover:bg-white p-2 rounded-full shadow-md transition-colors"
                            aria-label="Zoom image"
                        >
                            <FiZoomIn size={20} />
                        </button>
                    </div>
                </div>
                <div className="flex-1 lg:w-[45%] px-2 md:px-10">
                    <h2 className="text-3xl font-normal font-montserrat mb-4" style={{ color: textColor }}>{product.name}</h2>
                    <div className="flex flex-wrap items-center gap-4 mb-6">
                        {product.originalPrice > product.price && <span className="text-[var(--muted-text)] line-through text-lg font-semibold">Rs. {product.originalPrice.toLocaleString()}</span>}
                        <span className="text-xl font-medium font-montserrat" style={{ color: textColor }}>Rs. {product.price?.toLocaleString()}</span>
                        {discountPercent > 0 && <span className="text-[var(--primary-color)] text-base font-normal">Save {discountPercent}%</span>}
                    </div>
                    {isProductCompletelyOutOfStock && (
                        <div className="border rounded-sm p-4 text-center mb-6" style={{ backgroundColor: '#ef4444', borderColor: '#ef4444' }}>
                            <div className="text-white font-bold text-lg">SOLD OUT</div>
                        </div>
                    )}
                    {settings.saleBoxEnabled && !isProductCompletelyOutOfStock && (
                        <div className="border rounded-sm p-4 text-center mb-6" style={{ backgroundColor: settings.saleBoxColor, borderColor: settings.saleBoxColor }}>
                            <div className="text-white font-semibold text-base">{settings.saleBoxContent}</div>
                            <div className="text-[#fef2f2] font-bold text-lg">Shop Before it Ends</div>
                        </div>
                    )}
                    <div className="flex items-center gap-2 mb-6">
                        <div className="flex text-yellow-400">{[...Array(5)].map((_, i) => <span key={i}>★</span>)}</div>
                        <span className="text-gray-500 text-sm">{product.numReviews || 0} Reviews</span>
                    </div>
                    {colors.length > 0 && (
                        <div className="mb-6">
                            <div className="flex justify-between mb-2">
                                <span className="font-medium text-gray-700">COLOUR</span>
                                <span className="text-gray-500 text-sm">{selectedColor || 'Select'}</span>
                            </div>
                            <div className="flex flex-wrap gap-3">
                                {colors.map((color) => {
                                    const inStock = isColorInStock(color);
                                    return (
                                        <button
                                            key={color}
                                            onClick={() => inStock && setSelectedColor(color)}
                                            className={`px-6 py-2 border rounded-sm text-sm font-medium transition-colors ${
                                                selectedColor === color
                                                    ? 'bg-black text-white border-black'
                                                    : 'border-gray-300 text-gray-700 hover:border-gray-400'
                                            } ${!inStock ? 'opacity-50 line-through cursor-not-allowed' : ''}`}
                                            disabled={!inStock}
                                        >
                                            {color}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                    {sizes.length > 0 && (
                        <div className="mb-6">
                            <div className="flex justify-between mb-2">
                                <span className="font-medium text-gray-700">SIZE</span>
                            </div>
                            <div className="flex flex-wrap gap-3">
                                {sizes.map((size) => {
                                    const inStock = isSizeInStock(size);
                                    return (
                                        <button
                                            key={size}
                                            onClick={() => inStock && setSelectedSize(size)}
                                            className={`min-w-[48px] h-12 px-2 flex items-center justify-center border rounded-sm text-sm font-medium transition-colors ${
                                                selectedSize === size
                                                    ? 'bg-black text-white border-black'
                                                    : 'border-gray-300 text-gray-700 hover:border-gray-400'
                                            } ${!inStock ? 'opacity-50 line-through cursor-not-allowed' : ''}`}
                                            disabled={!inStock}
                                        >
                                            {size}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                    {sizes.length > 0 && (
                        <button
                            onClick={() => setShowSizeChart(true)}
                            className="flex items-center gap-2 mb-6 cursor-pointer hover:opacity-80"
                            style={{ color: textColor }}
                        >
                            <span className="uppercase text-sm font-medium tracking-widest">SIZE CHART</span>
                            <FaTape size={20} />
                        </button>
                    )}
                    <div className="mb-6">
                        <h4 className="uppercase text-sm font-medium tracking-widest mb-3" style={{ color: textColor }}>QUANTITY</h4>
                        <div className="inline-flex border border-gray-300 rounded-sm">
                            <button onClick={() => setQuantity(Math.max(1, quantity - 1))} className="w-10 h-10 flex items-center justify-center text-xl bg-white hover:bg-gray-50">−</button>
                            <div className="w-12 flex items-center justify-center border-x border-gray-300 text-lg">{quantity}</div>
                            <button 
                                onClick={() => setQuantity(q => (selectedVariant ? Math.min(selectedVariant.quantity, q + 1) : q + 1))} 
                                className="w-10 h-10 flex items-center justify-center text-xl bg-white hover:bg-gray-50"
                            >+</button>
                        </div>
                        <p className="text-xs text-gray-500 mt-2">Max available: {selectedVariant ? selectedVariant.quantity : 0}</p>
                    </div>
                    <div className="flex items-center gap-2 mb-6">
                        <div className={`w-2.5 h-2.5 rounded-full ${isVariantInStock ? 'bg-[#22c55e]' : 'bg-red-500'}`}></div>
                        <span style={{ color: textColor }}>{isVariantInStock ? `In stock (${selectedVariant?.quantity || 0} available)` : 'Out of stock'}</span>
                    </div>
                    <div className="flex items-start gap-3 mb-8">
                        <div className="mt-1">🚚</div>
                        <div><p className="font-medium text-[#333] text-sm">Get Free Delivery On Prepaid & Above Rs 3500 Orders. <br/> 100% Recycled Packaging & Eco-Friendly Deliveries</p></div>
                    </div>
                    <div className="border border-gray-200 rounded-sm mb-8">
                        <div className="border-b border-gray-200">
                            <div onClick={() => toggleAccordion('description')} className="flex justify-between items-center p-4 cursor-pointer bg-white hover:bg-gray-50">
                                <span className="uppercase text-sm font-medium tracking-widest" style={{ color: textColor }}>DESCRIPTION</span>
                                {openAccordion === 'description' ? <FiChevronUp /> : <FiChevronDown />}
                            </div>
                            <div className={`overflow-hidden transition-all duration-300 ${openAccordion === 'description' ? 'max-h-[500px] opacity-100 p-4' : 'max-h-0 opacity-0'}`}>
                                <p className="text-gray-600 text-sm leading-relaxed whitespace-pre-wrap">{product.description || product.shortDescription || "No description available."}</p>
                            </div>
                        </div>
                        <div>
                            <div onClick={() => toggleAccordion('shipping')} className="flex justify-between items-center p-4 cursor-pointer bg-white hover:bg-gray-50">
                                <span className="uppercase text-sm font-medium tracking-widest" style={{ color: textColor }}>SHIPPING INFORMATION</span>
                                {openAccordion === 'shipping' ? <FiChevronUp /> : <FiChevronDown />}
                            </div>
                            <div className={`overflow-hidden transition-all duration-300 ${openAccordion === 'shipping' ? 'max-h-[500px] opacity-100 p-4' : 'max-h-0 opacity-0'}`}>
                                <ul className="list-disc pl-4 text-gray-600 text-sm leading-relaxed">
                                    <li>Free shipping on orders above Rs. 3,500</li><li>Estimated delivery: 3-5 business days</li><li>Easy returns within 7 days</li>
                                </ul>
                            </div>
                        </div>
                    </div>

                    {/* Desktop buttons - visible only on lg and up */}
                    <div className="hidden lg:flex flex-col gap-3 mt-2">
                        <button onClick={handleAddToCart} disabled={!isVariantInStock || isProductCompletelyOutOfStock} className="w-full border-2 border-black bg-white text-black font-bold py-4 text-sm uppercase tracking-widest hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">ADD TO CART</button>
                        <button onClick={handleBuyNow} disabled={!isVariantInStock || isProductCompletelyOutOfStock} className="w-full text-white font-bold py-4 text-sm uppercase tracking-widest transition-colors disabled:opacity-50 disabled:cursor-not-allowed" style={{ backgroundColor: primaryColor }}>BUY IT NOW</button>
                    </div>
                </div>
            </div>

            {/* Mobile fixed bottom buttons - visible only below lg */}
            <div className="lg:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-3 z-50 shadow-[0_-4px_10px_rgba(0,0,0,0.05)]">
                <div className="flex flex-col gap-2 max-w-[1550px] mx-auto">
                    <button onClick={handleAddToCart} disabled={!isVariantInStock || isProductCompletelyOutOfStock} className="w-full border-2 border-black bg-white text-black font-bold py-3 text-sm uppercase tracking-widest hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">ADD TO CART</button>
                    <button onClick={handleBuyNow} disabled={!isVariantInStock || isProductCompletelyOutOfStock} className="w-full text-white font-bold py-3 text-sm uppercase tracking-widest transition-colors disabled:opacity-50 disabled:cursor-not-allowed" style={{ backgroundColor: primaryColor }}>BUY IT NOW</button>
                </div>
            </div>

            <GalleryModal
                isOpen={isGalleryOpen}
                images={images}
                currentIndex={galleryIndex}
                onClose={closeGallery}
                onPrev={prevGalleryImage}
                onNext={nextGalleryImage}
            />
            <SizeChartModal isOpen={showSizeChart} onClose={() => setShowSizeChart(false)} />
        </div>
    );
};

export default ProductDetail;